import { useState, useEffect, useRef, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { Capacitor } from '@capacitor/core';

type MessageCallback = (message: any) => void;
type ConnectionStatusCallback = (isConnected: boolean) => void;

interface UseStableSocketOptions {
  autoAuth?: boolean;
  connectionStatusCallback?: ConnectionStatusCallback;
  debug?: boolean;
  pingInterval?: number;
  maxReconnectAttempts?: number;
  connectionTimeout?: number;
  initialReconnectDelay?: number;
  maxReconnectDelay?: number;
  jitter?: number;
  silentReconnect?: boolean;
}

/**
 * Hook for managing a stable WebSocket connection with auto-reconnect and heartbeat
 * 
 * Features:
 * - Authentication with JWT token
 * - Auto reconnection with exponential backoff
 * - Message handling with type-based callbacks
 * - Connection status monitoring
 * - Ping/heartbeat for connection health checks
 * - Connection quality metrics
 */
export function useStableSocket({
  autoAuth = true,
  connectionStatusCallback,
  debug = false,
  pingInterval = 50000,
  maxReconnectAttempts = 20, 
  connectionTimeout = 15000,
  initialReconnectDelay = 1000,
  maxReconnectDelay = 30000,
  jitter = 2000,
  silentReconnect = true
}: UseStableSocketOptions = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState<'good' | 'poor' | 'offline'>('offline');
  const [latency, setLatency] = useState<number>(0);
  const [authStatus, setAuthStatus] = useState<'none' | 'pending' | 'authenticated' | 'failed'>('none');
  
  const { token, user } = useAuth();
  const { toast } = useToast();
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastPingTimeRef = useRef<number>(0);
  const reconnectAttemptsRef = useRef(0);
  const messageCallbacksRef = useRef<Map<string, MessageCallback>>(new Map());
  const forceCloseRef = useRef(false);

  // Log if debug is enabled
  const log = useCallback((...args: any[]) => {
    if (debug) {
      console.log('[WebSocket]', ...args);
    }
  }, [debug]);

  // Function to authenticate the connection
  const authenticate = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN || !token) {
      return;
    }

    log('Authenticating WebSocket connection');
    setAuthStatus('pending');
    
    wsRef.current.send(JSON.stringify({
      type: 'MESSAGING_AUTH',
      token
    }));
  }, [token, log]);

  // Handle reconnection logic
  const handleReconnect = useCallback(() => {
    if (forceCloseRef.current || reconnectAttemptsRef.current >= maxReconnectAttempts) {
      log('No more reconnection attempts');
      return;
    }

    reconnectAttemptsRef.current++;
    const delay = Math.min(
      initialReconnectDelay * Math.pow(2, reconnectAttemptsRef.current - 1),
      maxReconnectDelay
    ) + Math.random() * jitter;

    log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    reconnectTimeoutRef.current = setTimeout(() => {
      connect();
    }, delay);
  }, [maxReconnectAttempts, initialReconnectDelay, maxReconnectDelay, jitter, log]);

  // Function to establish connection
  const connect = useCallback(() => {
    if (!user && autoAuth) {
      log('No user available for authentication, skipping connection');
      return;
    }

    // Don't try to connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      log('WebSocket already connected');
      return;
    }

    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      log('WebSocket already connecting');
      return;
    }

    // Clean up any existing connection
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (e) {
        log('Error closing existing connection:', e);
      }
    }

    setIsConnecting(true);
    setConnectionQuality('offline');

    try {
      const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
      log('Creating WebSocket connection to:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      
      // Set connection timeout
      if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          log('Connection timeout, attempting reconnect');
          ws.close();
          setIsConnecting(false);
          handleReconnect();
        }
      }, connectionTimeout);

      ws.onopen = () => {
        log('WebSocket connection established');
        if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
        
        setIsConnecting(false);
        setIsConnected(true);
        setConnectionQuality('good');
        reconnectAttemptsRef.current = 0;

        // Authenticate if we have a token
        if (autoAuth && token) {
          authenticate();
        }
        
        // Set up ping interval
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            lastPingTimeRef.current = Date.now();
            ws.send(JSON.stringify({ type: 'PING' }));
          }
        }, pingInterval);
        
        // Call the external connection status callback if provided
        if (connectionStatusCallback) {
          connectionStatusCallback(true);
        }
      };

      ws.onclose = () => {
        log('WebSocket connection closed');
        if (connectionTimeoutRef.current) clearTimeout(connectionTimeoutRef.current);
        if (pingTimeoutRef.current) clearTimeout(pingTimeoutRef.current);
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        
        setIsConnecting(false);
        setIsConnected(false);
        setConnectionQuality('offline');
        setAuthStatus('none');
        
        if (connectionStatusCallback) {
          connectionStatusCallback(false);
        }
        
        if (!forceCloseRef.current) {
          handleReconnect();
        }
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[WebSocket] Received message:', data);
          
          // Handle internal message types
          if (data.type === 'MESSAGING_AUTH_SUCCESS') {
            log('Authentication successful');
            setAuthStatus('authenticated');
          } else if (data.type === 'CONNECTION_QUALITY') {
            setConnectionQuality(data.quality);
            setLatency(data.latency);
          } else if (data.type === 'PONG') {
            const latency = Date.now() - lastPingTimeRef.current;
            setLatency(latency);
          } else {
            // Forward message to registered callback
            const callback = messageCallbacksRef.current.get(data.type);
            if (callback) {
              callback(data);
            } else {
              console.log('[WebSocket] No handler registered for message type:', data.type);
            }
          }
        } catch (error) {
          console.error('Error parsing message:', error);
          console.error('Raw message:', event.data);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      log('Error creating WebSocket:', error);
      setIsConnecting(false);
      handleReconnect();
    }
  }, [
    user, 
    token, 
    autoAuth, 
    connectionTimeout, 
    pingInterval, 
    handleReconnect,
    connectionStatusCallback, 
    authenticate,
    log
  ]);

  // Register a callback for specific message types
  const registerMessageCallback = useCallback((type: string, callback: MessageCallback) => {
    messageCallbacksRef.current.set(type, callback);
    return () => {
      messageCallbacksRef.current.delete(type);
    };
  }, []);

  // Send a message through the WebSocket
  const sendMessage = useCallback((message: any) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      log('WebSocket is not connected');
      return false;
    }

    try {
      wsRef.current.send(JSON.stringify(message));
      return true;
    } catch (error) {
      log('Error sending message:', error);
      return false;
    }
  }, [log]);

  // Connect when component mounts or user/token changes
  useEffect(() => {
    connect();
    
    return () => {
      forceCloseRef.current = true;
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  return {
    isConnected,
    isConnecting,
    connectionQuality,
    latency,
    authStatus,
    sendMessage,
    registerMessageCallback
  };
}